/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type AxiosInstance } from 'axios';
import {
  type GCPProductVehicleImageResponse,
  type AcesGCPProductVehicleImageOptions,
} from '../interface';
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type QueryClient } from '@tanstack/react-query';

const URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v2/product-vehicle-image`;

const getAcesGcpProductVehicleImage = async (
  options: AcesGCPProductVehicleImageOptions,
  axiosInstance?: AxiosInstance
): Promise<GCPProductVehicleImageResponse> => {
  const response = await getAxios(axiosInstance).get<GCPProductVehicleImageResponse>(URL, {
    params: options,
    withCredentials: false,
  });
  return response.data;
};

const { useData: useAcesProductVehicleImageInfo, prefetch: prefetchAcesProductVehicleImageInfo } =
  createQuery<GCPProductVehicleImageResponse, AcesGCPProductVehicleImageOptions>(
    'getAcesGcpProductVehicleImage',
    async (options, axiosInstance) => getAcesGcpProductVehicleImage({ ...options }, axiosInstance)
  );

export const useGetAcesGcpProductVehicleImage = (props: {
  partCode: string;
  country: string;
  year: string;
  makeId?: number;
  modelId?: number;
  enabled: boolean;
}) => {
  const { partCode, country, year, makeId, modelId, enabled } = props;
  const options = {
    partCode,
    country,
    year,
    makeId,
    modelId,
    enabled,
  };

  return useAcesProductVehicleImageInfo(options);
};

export const prefetchAcesProductVehicleImage = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: AcesGCPProductVehicleImageOptions
) => {
  return prefetchAcesProductVehicleImageInfo(queryClient, options, axiosInstance);
};
